import { IMedicationDetails, IPrescriberDetails } from "../types/PrescriptionIngestionTypes";
// Imported from the leaf dateUtils (NOT PrescriptionHelpers) to avoid the PrescriptionHelpers <->
// PrescriptionDefaults import cycle: PrescriptionHelpers imports MEDICATIONS from here, and this
// module's createMedication() calls run at module-load, so importing from PrescriptionHelpers hits
// its temporal dead zone in isolated imports.
import { getCurrentDateString } from "../utils/dateUtils";

export const PHARMACY_DETAILS = {
    NPI: "1285084467",
    NCPDPID: "5919177"
};

/**
 * Message configuration for NewRx prescriptions
 */
export const NEWRX_MESSAGE_CONFIG = {
    TYPE: "NewRx",
    VERSION: "20170715",
    HEADER: {
        TO: "5919177",
        SENDER_SOFTWARE: {
            DEVELOPER: "1Life Healthcare, Inc.",
            PRODUCT: "1Life",
            VERSION: "2.0",
        }
    },
} as const;

/**
 * Default values for TransferRx workflow
 */
export const TRANSFER_RX_DEFAULTS = {
    PLACE_OF_SERVICE: "10",
    SELECTED_PRESCRIBER: "dr_kulkarni",
    PATIENT_GENDER: "M",
    SOURCE_PHARMACY: {
        NCPDP_ID: "0361573",
        NPI: "1356889513",
        BUSINESS_NAME: "CVS PHARMACY #10928",
        ADDRESS_LINE1: "909 E APACHE BLVD",
        CITY: "TEMPE",
        STATE: "AZ",
        ZIP_CODE: "85281-5817",
    },
} as const;

// Helper function to create medication with common defaults
const createMedication = (overrides: Partial<IMedicationDetails> & {
    drugDescription: string;
    ndc: string;
    drugDBCode?: string;
    drugDBQualifier?: string;
    strengthValue?: string;
    strengthUnit?: string;
    quantityValue?: string;
    quantityUnitCode?: string;
    quantityUnitDescription?: string;
    formCode?: string;
    formDescription?: string;
    daysSupply?: string;
    numberOfRefills?: string;
    sigText?: string;
    dispenseAsWritten?: number;
    writtenDate?: string;
    note?: string;
}): IMedicationDetails => {
    const {
        drugDescription,
        ndc,
        drugDBCode = "000000",
        drugDBQualifier = "SBD",
        strengthValue = "0",
        strengthUnit = "Milligram",
        quantityValue = "30",
        quantityUnitCode = "C48542",
        quantityUnitDescription = "Tablet",
        formCode = "C42998",
        formDescription = "Tablet",
        daysSupply = "30",
        numberOfRefills = "5",
        sigText = "Take 1 tablet by mouth daily.",
        dispenseAsWritten = 0,
        writtenDate = getCurrentDateString(),
        note,
        ...rest
    } = overrides;

    return {
        drugDescription,
        quantity: {
            value: quantityValue,
            unitOfMeasureCode: quantityUnitCode,
            unitOfMeasureDescription: quantityUnitDescription,
            qualifierCode: "38",
            qualifierDescription: "Original Quantity"
        },
        daysSupply,
        numberOfRefills,
        prescriberCheckedREMS: "A",
        ndc,
        drugDB: {
            code: drugDBCode,
            qualifier: drugDBQualifier
        },
        strength: {
            value: strengthValue,
            formCode: formCode,
            unitOfMeasureCode: "C48152",
            formDescription: formDescription,
            unitOfMeasureDescription: strengthUnit
        },
        sigText,
        dispenseAsWritten,
        writtenDate,
        ...(note && { note }),
        ...rest
    };
};

// prescribers for Kiosk, DO NOT EDIT
export const PRESCRIBERS: { [key: string]: IPrescriberDetails } = {
    "dr_kulkarni": {
        deaNumber: "FK1483368",
        npi: "1689828113",
        clinicName: "Beverly Hills",
        lastName: "Kulkarni",
        firstName: "Aarti",
        addressLine1: "9725 Wilshire Boulevard",
        city: "Beverly Hills",
        state: "CA",
        zipCode: "90212",
        phoneNumber: "8886636331",
        fax: [{ Number: "4152527176" }],
        spi: "6977486979012",
    },
    "dr_chao": {
        deaNumber: "BC8040583",
        npi: "1083087829",
        clinicName: "West LA",
        lastName: "Chao",
        firstName: "Fred",
        addressLine1: "12301 Wilshire Blvd Suite #100",
        city: "Los Angeles",
        state: "CA",
        zipCode: "90025",
        phoneNumber: "4243253244",
        fax: [{ Number: "4243253244" }],
        spi: "2466320676002",
    },
    "dr_richina": {
        deaNumber: "FR7154711",
        npi: "1750735395",
        clinicName: "Downtown LA",
        lastName: "Richina",
        firstName: "Jonathan",
        addressLine1: "700 South Flower Street Suite 2340",
        city: "Los Angeles",
        state: "CA",
        zipCode: "90017",
        phoneNumber: "3105002039",
        fax: [{ Number: "3233057149" }],
        spi: "6782630668002",
    },
    "dr_bhakta": {
        deaNumber: "FB1956397",
        npi: "1932361011",
        clinicName: "72nd & PCH",
        lastName: "Bhakta",
        firstName: "Dipesh",
        addressLine1: "6460 E Pacific Coast Hwy Suite D-210",
        city: "Long Beach",
        state: "CA",
        zipCode: "90803",
        phoneNumber: "5622709997",
        fax: [{ Number: "3103001052" }],
        spi: "6991553515007",
    },
    "dr_portiz": {
        deaNumber: "FP4376580",
        npi: "1780011726",
        clinicName: "West Hollywood",
        lastName: "Portiz",
        firstName: "Patrick",
        addressLine1: "8570 W Sunset Boulevard",
        city: "West Hollywood",
        state: "CA",
        zipCode: "90069",
        phoneNumber: "3105002041",
        fax: [{ Number: "3233057149" }],
        spi: "6890343497006",
    },
    "dr_kihm_icare": {
        deaNumber: "BK2277780",
        npi: "1639384175",
        clinicName: "Brigham Women's Health",
        lastName: "Kihm",
        firstName: "John",
        addressLine1: "3811 N Roxboro St",
        city: "Durham",
        state: "NC",
        zipCode: "27704-5800",
        phoneNumber: "9194713351",
        fax: [{ Number: "9194713351" }],
        spi: "6633950178005",
    }
};

export const MEDICATIONS: { [key: string]: IMedicationDetails } = {
    "abiraterone_acetate": createMedication({
        drugDescription: "Abiraterone Acetate 250 MG TAB",
        ndc: "72205003092",
        drugDBCode: "1100075",
        strengthValue: "250",
    }),
    "alprazolam": createMedication({
        drugDescription: "Alprazolam, 1MG Extended Release Tablet",
        ndc: "65862045560",
        drugDBCode: "433800",
        strengthValue: "1",
    }),
    "amlodipine_5mg": createMedication({
        drugDescription: "Amlodipine 5 MG Tablet",
        ndc: "69150011401",
        drugDBCode: "197361",
        strengthValue: "5",
    }),
    "amlodipine": createMedication({
        drugDescription: "Amlodipine 10 MG Tablet",
        ndc: "67877019990",
        drugDBCode: "308135",
        strengthValue: "10",
    }),
    "amoxicillin": createMedication({
        drugDescription: "Amoxicillin 500 MG Tablet",
        ndc: "65862001401",
        drugDBCode: "309888",
        strengthValue: "125",
        strengthUnit: "Microgram"
    }),
    "amoxicillin_clavulanate": createMedication({
        drugDescription: "Amoxicillin 875 MG / clavulanate 125 MG Oral Tablet",
        ndc: "65862050320",
        drugDBCode: "562508",
        strengthValue: "875-125",
        strengthUnit: "Milligram",
        quantityValue: "10",
        daysSupply: "10",
        sigText: "Take 1 tablet by mouth twice daily.",
    }),
    "atorvastatin": createMedication({
        drugDescription: "Atorvastatin 20 MG Tablet",
        ndc: "31722042510",
        drugDBCode: "617310",
        strengthValue: "20",
    }),
    "atorvastatin_10mg": createMedication({
        drugDescription: "Atorvastatin 10 MG Tablet",
        ndc: "00071015523",
        drugDBCode: "617312",
        drugDBQualifier: "FG",
        strengthValue: "10",
    }),
    "atorvastatin_40mg": createMedication({
        drugDescription: "Atorvastatin 40 MG Tablet",
        ndc: "60505258008",
        drugDBCode: "617311",
        drugDBQualifier: "FG",
        strengthValue: "40",
    }),
    "atorvastatin_80mg": createMedication({
        drugDescription: "Atorvastatin 80 MG Tablet",
        ndc: "42385094301",
        drugDBCode: "259255",
        drugDBQualifier: "FG",
        strengthValue: "80",
    }),
    "bupropion": createMedication({
        drugDescription: "Bupropion 24 HR hydrochloride 150 MG Extended Release Oral Tablet",
        ndc: "68180031909",
        drugDBCode: "993541",
        strengthValue: "150",
        quantityValue: "90",
        daysSupply: "90",
        sigText: "Take 1 tablet by mouth daily.",
    }),
    "buspirone": createMedication({
        drugDescription: "Buspirone 10 MG TAB",
        ndc: "29300024505",
        drugDBCode: "866083",
        strengthValue: "10",
    }),
    "carvedilol": createMedication({
        drugDescription: "Carvedilol 3.125 MG TAB",
        ndc: "65862014201",
        drugDBCode: "686924",
        strengthValue: "3.125",
    }),
    "carvedilol_25mg": createMedication({
        drugDescription: "Carvedilol 25 MG TAB",
        ndc: "68001015203",
        drugDBCode: "200033",
        strengthValue: "25",
    }),
    "celecoxib": createMedication({
        drugDescription: "Celecoxib 200 MG CAP",
        ndc: "50228015805",
        drugDBCode: "205323",
        strengthValue: "200",
    }),
    "citalopram": createMedication({
        drugDescription: "Citalopram 10 MG TAB",
        ndc: "69097082212",
        drugDBCode: "283672",
        strengthValue: "10",
    }),
    "dexcomG7Receiver": createMedication({
        drugDescription: "Dexcom G7 Receiver Device",
        ndc: "08627007801",
        drugDBCode: "AP00283",
        drugDBQualifier: "SCD", // Standard Semantic Clinical Drug qualifier used for e-prescribing
        strengthValue: "1",
        strengthUnit: "Device",
        quantityValue: "1",
        quantityUnitCode: "C64933", // NCIT code for "Each" or "Unit"
        quantityUnitDescription: "Each",
        formCode: "C42944", // Represents the physical device unit
        formDescription: "Device",
        daysSupply: "365",
        sigText: "Use as directed to monitor glucose levels; display results"
    }),
    "estradiol": createMedication({
        drugDescription: "Lo Loestrin Fe 28-day, 10MCG - 1MG Tablet",
        ndc: "00430042014",
        drugDBCode: "1037185",
        strengthValue: "75",
        quantityValue: "28",
        daysSupply: "28",
        numberOfRefills: "3",
    }),
    "estradiol_2mg": createMedication({
        drugDescription: "Estradiol, TABLET, 2 MG",
        ndc: "70954056620",
        drugDBCode: "197659",
        strengthValue: "2",
       
    }),
    "fluconazole": createMedication({
        drugDescription: "Fluconazole 150 MG Oral Tablet",
        ndc: "57237000511",
        drugDBCode: "197699",
        strengthValue: "150",
        quantityValue: "2",
        daysSupply: "2",
        numberOfRefills: "0",
        sigText: "Take 1 tablet by mouth as directed.",
    }),
    "fluticasone_salmeterol": createMedication({
        drugDescription: "Fluticasone Propionate 115 MCG-Salmeterol 21 MCG/Actuation HFA Inhaler (Advair HFA)",
        ndc: "66993008796",
        drugDBCode: "896244",
        drugDBQualifier: "SCD",
        strengthValue: "115-21",
        strengthUnit: "MCG/Actuation",
        quantityValue: "1",
        quantityUnitCode: "C64933",
        quantityUnitDescription: "Each",
        formCode: "C42944",
        formDescription: "Inhalant",
    }),
    "folic_acid": createMedication({
        drugDescription: "Folic Acid Rx, Tablet, 1 MG",
        ndc: "64125012710",
        drugDBCode: "310410",
        strengthValue: "1",
    }),
    "glucosamine_chondroitin_msm": createMedication({
        drugDescription: "Glucosamine - Chondroitin - MSM 500 MG - 67 MG - 500 MG TAB",
        ndc: "74098527291",
        drugDBCode: "AP00469",
        strengthValue: "500-67-500",
    }),
    "hydroxychloroquine": createMedication({
        drugDescription: "Hydroxychloroquine 200 MG TAB",
        ndc: "68382009605",
        drugDBCode: "979092",
        strengthValue: "200",
    }),
    "lipitor": createMedication({
        drugDescription: "Lipitor 20 MG Tablet",
        ndc: "00071015623",
        drugDBCode: "617318",
        strengthValue: "20",
    }),
    "lipitor_10mg": createMedication({
        drugDescription: "Lipitor 10 MG Tablet",
        ndc: "00071015523",
        drugDBCode: "617314",
        strengthValue: "10",
    }),
    "lisinopril": createMedication({
        drugDescription: "Lisinopril 10 MG Tablet",
        ndc: "68001033408",
        drugDBCode: "314076",
        drugDBQualifier: "SCD",
        strengthValue: "10",
    }),
    "lyrica": createMedication({
        drugDescription: "Lyrica 25 MG CAP",
        ndc: "58151023677",
        drugDBCode: "607026",
        strengthValue: "25",
    }),
    "metformin": createMedication({
        drugDescription: "Metformin 1000 MG TAB",
        ndc: "69367038310",
        drugDBCode: "861004",
        strengthValue: "1000",
    }),
    "mysoline": createMedication({
        drugDescription: "Mysoline 50 MG Oral Tablet",
        ndc: "66490069010",
        drugDBCode: "207478",
        strengthValue: "50",
    }),
    "nitrofurantoin": createMedication({
        drugDescription: "Nitrofurantoin Mono/Macro 100 MG CAP",
        ndc: "13811071910",
        drugDBCode: "1648755",
        strengthValue: "100",
    }),
    "premarin": createMedication({
        drugDescription: "Premarin 0.45 MG Tablet",
        ndc: "00046110181",
        drugDBCode: "404550",
        strengthValue: "0.45",
    }),
    "progesterone": createMedication({
        drugDescription: "Progesterone 200 MG Oral Capsule",
        ndc: "69452023420",
        drugDBCode: "312641",
        strengthValue: "200",
        quantityUnitCode: "C48542",
        quantityUnitDescription: "Capsule",
        formCode: "C25158",
        formDescription: "Capsule",
        sigText: "Take 1 capsule by mouth daily.",
    }),
    "sildenafil": createMedication({
        drugDescription: "Sildenafil, TABLET, 25 MG",
        ndc: "31722070930",
        drugDBCode: "314228",
        strengthValue: "25",
    }),
    "turmeric": createMedication({
        drugDescription: "Turmeric 500 MG CAP",
        ndc: "54629090746",
        drugDBCode: "AP00253",
        strengthValue: "500",
    }),
    "venlafaxine": createMedication({
        drugDescription: "Venlafaxine 37.5 MG ER CAP",
        ndc: "65862052799",
        drugDBCode: "313583",
        strengthValue: "37.5",
    }),
    "wegovy": createMedication({
        drugDescription: "Wegovy, 0.5 MG/0.5 ML Pen Injector",
        ndc: "00169450514",
        drugDBCode: "2553603",
        drugDBQualifier: "SCD",
        strengthValue: "0.5",
        strengthUnit: "MG/0.5ML",
        quantityValue: "4",
        quantityUnitCode: "C64933",
        quantityUnitDescription: "Each",
        formCode: "C42946",
        formDescription: "Injection",
        daysSupply: "28",
        sigText: "Inject 0.5 mL subcutaneously once weekly for weight management.",
    }),
    "zepbound": createMedication({
        drugDescription: "Zepbound, 10 MG/0.5 ML Pen Injector",
        ndc: "00002246080",
        drugDBCode: "2669718",
        drugDBQualifier: "SCD",
        strengthValue: "10",
        strengthUnit: "MG/0.5ML",
        quantityValue: "4",
        quantityUnitCode: "C64933",
        quantityUnitDescription: "Each",
        formCode: "C42946",
        formDescription: "Injection",
        daysSupply: "30",
        sigText: "Inject 0.5 mL subcutaneously once weekly for weight management.",
    }),
    "spiriva_handihaler": createMedication({
        drugDescription: "Spiriva HandiHaler 18 MCG Inhalation Powder",
        ndc: "00597007541",
        drugDBCode: "580261",
        strengthValue: "18",
        strengthUnit: "Microgram",
        quantityValue: "30",
        quantityUnitCode: "C48542",
        quantityUnitDescription: "Capsule",
        formCode: "C42944",
        formDescription: "Inhalant",
        sigText: "Inhale contents of 1 capsule once daily using HandiHaler device",
        note: "Clinical Trial Rx - iCare",
    }),
    "symbicort_160": createMedication({
        drugDescription: "Symbicort 160 MCG - 4.5 MCG INH",
        ndc: "00186037020",
        drugDBCode: "896209",
        strengthValue: "160",
        strengthUnit: "Microgram",
        quantityValue: "1",
        quantityUnitCode: "C48480",
        quantityUnitDescription: "Inhaler",
        formCode: "C42944",
        formDescription: "Inhalant",
        sigText: "Inhale 2 puffs by mouth twice daily",
        note: "Clinical Trial Rx - iCare",
    }),
};